import { ALL_VERTICALS, GLOBAL_COUNTRY, type ScopeQuery, parseScope } from "@app/shared"
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react"

/**
 * App-wide scope filter (country × vertical).
 *
 * Held once at the app root and read by every screen — the dashboard, the
 * list views, and the topbar ScopeSelector. State persists to localStorage
 * (mirroring the theme `gm:theme` precedent) so a chosen scope survives
 * navigation + refresh. The `params` field collapses the `global`/`all`
 * sentinels to absent so it can be spread straight into an API query.
 */
const STORAGE_KEY = "gm:scope"

export interface ScopeState {
	/** A 2-char country code, or `GLOBAL_COUNTRY` ("global") for all. */
	country: string
	/** A vertical id, or `ALL_VERTICALS` ("all") for consolidated. */
	vertical: string
}

const DEFAULT_SCOPE: ScopeState = { country: GLOBAL_COUNTRY, vertical: ALL_VERTICALS }

export interface ScopeContextValue extends ScopeState {
	setCountry: (country: string) => void
	setVertical: (vertical: string) => void
	clear: () => void
	/** True when neither dimension filters (global, consolidated view). */
	isGlobal: boolean
	/** API query params — `global`/`all` sentinels collapse to undefined. */
	params: ScopeQuery
}

const ScopeContext = createContext<ScopeContextValue | null>(null)

function readStored(): ScopeState {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return DEFAULT_SCOPE
		const parsed = JSON.parse(raw) as Partial<ScopeState>
		return {
			country: typeof parsed.country === "string" ? parsed.country : GLOBAL_COUNTRY,
			vertical: typeof parsed.vertical === "string" ? parsed.vertical : ALL_VERTICALS,
		}
	} catch {
		return DEFAULT_SCOPE
	}
}

export function ScopeProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<ScopeState>(readStored)

	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
		} catch {
			// localStorage unavailable (private mode / quota) — scope still works
			// in-memory for the session; persistence is best-effort.
		}
	}, [state])

	const setCountry = useCallback((country: string) => setState((s) => ({ ...s, country })), [])
	const setVertical = useCallback((vertical: string) => setState((s) => ({ ...s, vertical })), [])
	const clear = useCallback(() => setState(DEFAULT_SCOPE), [])

	const value = useMemo<ScopeContextValue>(() => {
		const params = parseScope({ country: state.country, vertical: state.vertical })
		return {
			...state,
			setCountry,
			setVertical,
			clear,
			isGlobal: params.country === undefined && params.vertical === undefined,
			params,
		}
	}, [state, setCountry, setVertical, clear])

	return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
}

export function useScope(): ScopeContextValue {
	const ctx = useContext(ScopeContext)
	if (!ctx) {
		throw new Error("useScope must be used within a ScopeProvider")
	}
	return ctx
}
